import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Avatar, Chip } from "@mui/material";
import BaseCard from "../../../components/ui/BaseCard";
import BaseButton from "../../../components/ui/BaseButton";
import BaseDataTableV2 from "../../../components/ui/BaseDataTableV2";
import BaseTextField from "../../../components/ui/BaseTextField";
import BaseConfirmModal from "../../../components/ui/BaseConfirmModal";
import BaseDropdownV2 from "../../../components/ui/BaseDropdownV2";
import BaseTooltip from "../../../components/ui/BaseTooltip";
import CreateCustomerUserModal from "./CreateCustomerUserModal";
import CompleteProfileModal from "../../WorkJobDetails/CompleteProfileModal";
import {
  getCustomerUsers,
  removeCustomerPortalUsers,
  setCustomerPortalUsersActive,
} from "../../../services/api/customerUsers";
import { showToast } from "../../../store/toast/toastSlice";
import { debounce, formatPhoneNumber } from "../../../utils/helpers";
import { useCurrentUser } from "../../../hooks/useCurrentUser";
import useAuth from "../../../hooks/useAuth";
import { joinNonEmpty } from "../../../utils/displayValue";
import dayjs from "dayjs";

const menuOptions = [
  { label: "Activate", value: "ACTIVATE" },
  { label: "Deactivate", value: "DEACTIVATE" },
  { label: "Remove", value: "REMOVE" },
];

const canActivate = (status) => status === "DEACTIVATED";
const canDeactivate = (status) => ["APPROVED", "ACTIVATED"].includes(status);

const CANNOT_ACTIVATE_NOTICE = {
  title: "Cannot activate",
  message: "Only a deactivated account can be activated.",
};
const CANNOT_DEACTIVATE_NOTICE = {
  title: "Cannot deactivate",
  message: "Only an active account can be deactivated.",
};

const CustomerUsersTable = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [pagination, setPagination] = useState({ page: 0, pageSize: 10 });
  const [search, setSearch] = useState("");
  const [userToRemove, setUserToRemove] = useState(null);
  const [removeReason, setRemoveReason] = useState("");
  const [isCreateUserOpen, setIsCreateUserOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  // Which ids a pending activate/deactivate applies to — a row action and a bulk
  // action are the same call, so they share one confirmation.
  const [statusChange, setStatusChange] = useState(null);
  // Why an activate/deactivate could not run, shown as its own message.
  const [statusNotice, setStatusNotice] = useState(null);
  const [isProfileCompleted, setIsProfileCompleted] = useState(false);
  const { userData } = useCurrentUser();
  const { authRole } = useAuth();

  const queryParams = {
    page: pagination.page + 1,
    limit: pagination.pageSize,
    search,
  };

  const { isPending, data } = useQuery({
    queryKey: ["customerUsers", queryParams],
    queryFn: () => getCustomerUsers(queryParams),
    placeholderData: (prev) => prev,
  });

  const { mutateAsync: removeUsersMutation, isPending: isRemoving } = useMutation({
    mutationFn: ({ ids, reason }) => removeCustomerPortalUsers(ids, reason),
  });

  const { mutateAsync: setUsersActiveMutation, isPending: isUpdatingStatus } = useMutation({
    mutationFn: ({ ids, active }) => setCustomerPortalUsersActive(ids, active),
  });

  const debouncedSearchRef = useRef(
    debounce((value) => {
      setSearch(value);
      setPagination((p) => ({ ...p, page: 0 }));
    }, 500),
  );

  const users = data?.data?.data || [];
  const totalCount = data?.data?.totalCount || 0;

  // A mixed selection acts on the rows the action applies to and says how many it
  // left alone, rather than failing the whole batch.
  const selectedRows = users.filter((user) => selectedIds.includes(user.id));
  const activatableIds = selectedRows.filter((user) => canActivate(user.status)).map((u) => u.id);
  const deactivatableIds = selectedRows
    .filter((user) => canDeactivate(user.status))
    .map((user) => user.id);

  const handleItemClick = (action, user) => {
    if (action === "REMOVE") {
      setRemoveReason("");
      setUserToRemove([user.id]);
      return;
    }

    if (action === "ACTIVATE") {
      if (!canActivate(user.status)) {
        setStatusNotice(CANNOT_ACTIVATE_NOTICE);
        return;
      }
      setStatusChange({ ids: [user.id], active: true });
      return;
    }

    if (action === "DEACTIVATE") {
      if (!canDeactivate(user.status)) {
        setStatusNotice(CANNOT_DEACTIVATE_NOTICE);
        return;
      }
      setStatusChange({ ids: [user.id], active: false });
    }
  };

  const closeRemoveModal = () => {
    setUserToRemove(null);
    setRemoveReason("");
  };

  const handleStatusChange = async () => {
    try {
      const { ids, active } = statusChange;
      await setUsersActiveMutation({ ids, active });
      dispatch(
        showToast({
          message: `${ids.length > 1 ? "Users" : "User"} ${active ? "activated" : "deactivated"} successfully`,
          type: "success",
        }),
      );
      queryClient.invalidateQueries({ queryKey: ["customerUsers"] });
      setStatusChange(null);
      setSelectedIds([]);
    } catch (error) {
      dispatch(
        showToast({
          message:
            error?.response?.data?.message ||
            `Failed to ${statusChange?.active ? "activate" : "deactivate"} the selected users`,
          type: "error",
        }),
      );
    }
  };

  const handleRemove = async () => {
    // Same rule as the platform's delete dialog: a reason is mandatory. The API
    // re-validates it, so this is just the earlier, friendlier stop.
    if (!removeReason?.trim()) {
      dispatch(
        showToast({
          message: "Please provide a reason before deleting the user.",
          type: "error",
        }),
      );
      return;
    }

    try {
      await removeUsersMutation({ ids: userToRemove, reason: removeReason.trim() });
      dispatch(
        showToast({
          message: `${userToRemove.length > 1 ? "Users" : "User"} removed successfully`,
          type: "success",
        }),
      );
      queryClient.invalidateQueries({ queryKey: ["customerUsers"] });
      setSelectedIds([]);
      closeRemoveModal();
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message || "Failed to remove the selected users",
          type: "error",
        }),
      );
    }
  };

  const columns = [
    {
      field: "id",
      headerName: "ID",
      flex: 0.5,
      renderCell: (params) => (
        <p
          className="table-font-size font-normal text-primary ml-2 cursor-pointer"
          onClick={() => navigate(`/user/details/profile/${params.row.id}`)}
        >
          {params.row.id}
        </p>
      ),
    },
    {
      field: "name",
      headerName: "Name",
      flex: 1.5,
      renderCell: (params) => (
        <div
          className="flex items-center gap-3 cursor-pointer"
          onClick={() => navigate(`/user/details/profile/${params.row.id}`)}
        >
          <Avatar sx={{ width: 28, height: 28, fontSize: 12 }} src={params.row.profilePicture}>
            {params.row.firstName?.[0]}
            {params.row.lastName?.[0]}
          </Avatar>
          <p className="table-font-size font-normal capitalize text-primary">
            {params.row.firstName} {params.row.lastName}
          </p>
        </div>
      ),
    },
    {
      field: "address",
      headerName: "Address",
      flex: 1.5,
      renderCell: (params) => {
        const address = params.row.personalAddress;
        const fullAddress = joinNonEmpty([address?.addressLine1, address?.country]);

        if (!fullAddress) return <p className="table-font-size cursor-default"></p>;

        return (
          <BaseTooltip placement="top" content={<p className="text-white">{fullAddress}</p>}>
            <p className="table-font-size cursor-default truncate overflow-hidden whitespace-nowrap">
              {`${address?.addressLine1 || ""}`}
            </p>
          </BaseTooltip>
        );
      },
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1.5,
      renderCell: (params) => <p className="table-font-size">{params.row.email}</p>,
    },
    {
      field: "phoneNumber",
      headerName: "Mobile",
      flex: 1.5,
      renderCell: (params) => (
        <p className="table-font-size">{formatPhoneNumber(params.row.phoneNumber) || "—"}</p>
      ),
    },
    {
      field: "createdAt",
      headerName: "Date",
      flex: 1.5,
      renderCell: (params) => (
        <p className="table-font-size">{dayjs(params.row.createdAt).format("DD-MM-YYYY")}</p>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) =>
        params.row.status === "APPROVED" || params.row.status === "ACTIVATED" ? (
          <Chip label="Active" color="primary" size="small" className="w-24 justify-center" />
        ) : params.row.status === "DEACTIVATED" ? (
          <Chip label="In-Active" color="error" size="small" className="w-24 justify-center" />
        ) : (
          <Chip
            label={params.row.status === "SUBMITTED" ? "Submitted" : "Pending"}
            color="primary"
            size="small"
            variant="outlined"
            className="w-24 justify-center"
          />
        ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.6,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        // Only what the row's status allows: no Activate on an active or pending
        // account, no Deactivate on one already switched off.
        const options = menuOptions.filter((option) => {
          if (option.value === "ACTIVATE") return canActivate(params.row.status);
          if (option.value === "DEACTIVATE") return canDeactivate(params.row.status);
          return true;
        });

        return (
          <BaseDropdownV2
            menuOptions={options}
            triggerElement={<i className="fa-solid fa-ellipsis cursor-pointer ml-4 text-base"></i>}
            onItemClick={(item) => handleItemClick(item, params.row)}
          />
        );
      },
    },
  ];

  return (
    <>
      <BaseCard elevation={0} className="py-4 px-5 rounded-xl shadow-lg ">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 w-full">
          <div className="w-full sm:w-2/6">
            {/* type="search" + autoComplete="off" and a name that reads as nothing
                personal: without them the browser saw a bare text box on a page
                full of contact fields and offered to autofill an email address
                into the listing's search. */}
            <BaseTextField
              id="customerUsersSearch"
              name="customerUsersSearch"
              type="search"
              autoComplete="off"
              placeholder="Search by name, email or ID"
              onChange={(e) => debouncedSearchRef.current(e.target.value)}
              startAdornment={<i className="fa-light fa-magnifying-glass text-lg"></i>}
            />
          </div>

          <div className="flex items-center gap-3 w-full sm:w-auto">
            {selectedIds.length > 1 && (
              <>
                <p className="text-sm whitespace-nowrap">{selectedIds.length} selected</p>
                <BaseButton
                  variant="outlined"
                  color="primary"
                  className="!py-[5px] whitespace-nowrap"
                  onClick={() => {
                    if (!activatableIds.length) {
                      setStatusNotice(CANNOT_ACTIVATE_NOTICE);
                      return;
                    }
                    setStatusChange({
                      ids: activatableIds,
                      active: true,
                      skippedIds: selectedIds.filter((id) => !activatableIds.includes(id)),
                    });
                  }}
                >
                  Activate
                </BaseButton>
                <BaseButton
                  variant="outlined"
                  color="primary"
                  className="!py-[5px] whitespace-nowrap"
                  onClick={() => {
                    if (!deactivatableIds.length) {
                      setStatusNotice(CANNOT_DEACTIVATE_NOTICE);
                      return;
                    }
                    setStatusChange({
                      ids: deactivatableIds,
                      active: false,
                      skippedIds: selectedIds.filter((id) => !deactivatableIds.includes(id)),
                    });
                  }}
                >
                  Deactivate
                </BaseButton>
                <BaseButton
                  variant="outlined"
                  color="error"
                  // BaseButton's outlined hover builds a class name from the colour
                  // prop, which Tailwind never compiles — so the fill is set here.
                  className="!py-[5px] whitespace-nowrap hover:!bg-red-600 hover:!text-white"
                  onClick={() => {
                    setRemoveReason("");
                    setUserToRemove(selectedIds);
                  }}
                >
                  Remove
                </BaseButton>
              </>
            )}

            <BaseButton
              color="primary"
              className="!py-[5px] whitespace-nowrap w-full sm:w-auto"
              onClick={() => {
                // Adding users waits on approval, like every other action here.
                if (userData?.status !== "APPROVED") {
                  setIsProfileCompleted(true);
                  return;
                }
                setIsCreateUserOpen(true);
              }}
            >
              Add User
            </BaseButton>
          </div>
        </div>
      </BaseCard>

      <BaseDataTableV2
        className="max-h-[calc(100vh-320px)] overflow-auto"
        rowHeight={38}
        columns={columns}
        data={users}
        isLoading={isPending}
        disableColumnResize={false}
        disableRowSelectionOnClick
        checkboxSelection
        rowSelectionModel={selectedIds}
        onRowSelectionModelChange={(ids) => setSelectedIds(ids)}
        hideFooter={false}
        pagination
        rowCount={totalCount}
        pageSizeOptions={[10, 20, 50, 100]}
        paginationModel={pagination}
        onPaginationModelChange={setPagination}
        paginationMode="server"
      />

      {isProfileCompleted && (
        <CompleteProfileModal
          action="add a user"
          submittedMessage="Sorry! You can add your users after your profile verification."
          isProfileCompleted={isProfileCompleted}
          setIsProfileCompleted={setIsProfileCompleted}
          status={userData?.status}
          authRole={authRole}
        />
      )}

      {isCreateUserOpen && (
        <CreateCustomerUserModal
          open={isCreateUserOpen}
          onClose={() => setIsCreateUserOpen(false)}
        />
      )}

      {!!statusNotice && (
        <BaseConfirmModal
          open={!!statusNotice}
          onClose={() => setStatusNotice(null)}
          onConfirm={() => setStatusNotice(null)}
          title={statusNotice.title}
          description={statusNotice.message}
          confirmText="OK"
          confirmButtonColor="primary"
          hideCancel
        />
      )}

      {!!statusChange && (
        <BaseConfirmModal
          open={!!statusChange}
          onClose={() => setStatusChange(null)}
          onConfirm={handleStatusChange}
          isLoading={isUpdatingStatus}
          title={`Are you sure you want to ${statusChange.active ? "activate" : "deactivate"} ${
            statusChange.ids.length > 1 ? `these ${statusChange.ids.length} users` : "this user"
          }?`}
          description={
            <>
              <p className="mt-1">
                {statusChange.active
                  ? "Once activated, they regain access to the portal."
                  : "Deactivation is temporary: they are signed out and lose access to the portal until you activate them again."}
              </p>
              {!!statusChange.skippedIds?.length && (
                <p className="mb-2 mt-4 rounded-md border border-gray-200 bg-gray-100 px-4 py-3 leading-relaxed text-primary">
                  <span className="font-medium">Note:</span>{" "}
                  {statusChange.active
                    ? "Only deactivated users can be activated, so "
                    : "Only active users can be deactivated, so "}
                  {statusChange.skippedIds.length > 1 ? "IDs " : "ID "}
                  <span className="font-medium">{statusChange.skippedIds.join(", ")}</span>
                  {statusChange.skippedIds.length > 1 ? " are" : " is"} skipped.
                </p>
              )}
            </>
          }
          confirmText={statusChange.active ? "Activate" : "Deactivate"}
          confirmButtonColor={statusChange.active ? "primary" : "error"}
        />
      )}

      {!!userToRemove && (
        <BaseConfirmModal
          open={!!userToRemove}
          onClose={closeRemoveModal}
          onConfirm={handleRemove}
          isLoading={isRemoving}
          title={`Are you sure you want to delete ${
            userToRemove.length > 1 ? `these ${userToRemove.length} users` : "this user"
          }?`}
          description={
            <>
              <BaseTextField
                label="Reason"
                name="reason"
                placeholder="Reason"
                value={removeReason}
                onChange={(e) => setRemoveReason(e.target.value)}
                labelShrink={false}
                optional={false}
                isClearable
                isDefaultField={false}
              />
              <p className="mt-3 ">
                {userToRemove.length > 1 ? "These accounts are" : "This account is"} deleted
                permanently and {userToRemove.length > 1 ? "lose" : "loses"} access to the portal.
                This action cannot be undone.
              </p>
            </>
          }
          confirmText="Delete"
          confirmButtonColor="error"
        />
      )}
    </>
  );
};

export default CustomerUsersTable;
